import { useState } from 'react';
import { PencilIcon } from './icons';
import { TimePickerInput } from './TimePickerInput';

const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export function CustomiseDayTable({
  weeklySchedule,
  onUpdateWeekly,
  holidays,
  onUpdateHoliday,
}) {
  // Use local state only for which tab is active
  const [activeDay, setActiveDay] = useState('Monday');

  const isDateOverride = !DAYS.includes(activeDay);

  let dayConfig;
  if (isDateOverride) {
    dayConfig = holidays?.find((h) => h.date === activeDay) || {};
  } else {
    dayConfig = weeklySchedule?.[activeDay.toLowerCase()] || {};
  }

  const isClosed =
    dayConfig.type === 'closed' || dayConfig.type === 'full_day_off';
  const isHalfDay = dayConfig.type === 'half_day';
  const dayType = isClosed ? 'Closed' : isHalfDay ? 'Half day' : 'Full day';
  const startTime = dayConfig.working_hours_start || '09:00';
  const endTime = dayConfig.working_hours_end || '18:00';
  const holidayReason = dayConfig.name || dayConfig.reason || '';

  const handleSetDayType = (type) => {
    let newType =
      type === 'Closed'
        ? isDateOverride
          ? 'full_day_off'
          : 'closed'
        : type === 'Half day'
          ? 'half_day'
          : 'working';
    if (isDateOverride) {
      onUpdateHoliday?.(activeDay, { ...dayConfig, type: newType });
    } else {
      onUpdateWeekly(activeDay.toLowerCase(), { ...dayConfig, type: newType });
    }
  };

  const handleSetStartTime = (v) => {
    if (isDateOverride) {
      onUpdateHoliday?.(activeDay, { ...dayConfig, working_hours_start: v });
    } else {
      onUpdateWeekly(activeDay.toLowerCase(), {
        ...dayConfig,
        working_hours_start: v,
      });
    }
  };

  const handleSetEndTime = (v) => {
    if (isDateOverride) {
      onUpdateHoliday?.(activeDay, { ...dayConfig, working_hours_end: v });
    } else {
      onUpdateWeekly(activeDay.toLowerCase(), {
        ...dayConfig,
        working_hours_end: v,
      });
    }
  };

  const handleSetHolidayReason = (v) => {
    if (!isDateOverride) return;
    onUpdateHoliday?.(activeDay, { ...dayConfig, name: v, reason: v });
  };

  return (
    <div className="mt-4 pt-4 border-t border-[#E5E7EB] flex flex-col gap-6">
      {/* Filters & Save */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {DAYS.map((d) => {
            const isActive = activeDay === d;
            const dKey = d.toLowerCase();
            const dConfig = weeklySchedule?.[dKey] || {};
            const dIsClosed = dConfig.type === 'closed';

            let tabClassName =
              'h-8 px-4 rounded-lg border text-xs font-medium transition-colors ';
            if (isActive) {
              tabClassName += dIsClosed
                ? 'bg-white border-status-error text-status-error'
                : 'bg-primary border-primary text-white';
            } else {
              tabClassName += dIsClosed
                ? 'bg-white border-status-error text-status-error hover:bg-status-error/10'
                : 'bg-white border-border-default text-text-secondary hover:border-primary hover:text-primary';
            }

            return (
              <button
                key={d}
                onClick={() => setActiveDay(d)}
                className={tabClassName}
              >
                {d}
              </button>
            );
          })}
          {isDateOverride && (
            <button className="h-8 px-4 rounded-lg border bg-primary border-primary text-white text-xs font-medium transition-colors">
              {activeDay}
            </button>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="flex flex-col gap-4">
        {/* Day Type Buttons */}
        <div className="grid grid-cols-3 gap-3">
          {['Full day', 'Half day', 'Closed'].map((type) => {
            const isSelected = dayType === type;

            let colorClasses = '';
            if (isSelected) {
              if (type === 'Closed') {
                colorClasses =
                  'bg-status-error/10 border-status-error text-status-error';
              } else if (type === 'Half day') {
                colorClasses =
                  'bg-[#B224EF]/10 border-[#B224EF] text-[#B224EF]';
              } else {
                colorClasses = 'bg-primary/10 border-primary text-primary';
              }
            } else {
              if (type === 'Closed') {
                colorClasses =
                  'bg-white border-border-default text-text-secondary hover:border-status-error hover:text-status-error';
              } else if (type === 'Half day') {
                colorClasses =
                  'bg-white border-border-default text-text-secondary hover:border-[#B224EF] hover:text-[#B224EF]';
              } else {
                colorClasses =
                  'bg-white border-border-default text-text-secondary hover:border-primary hover:text-primary';
              }
            }

            return (
              <button
                key={type}
                type="button"
                onClick={() => handleSetDayType(type)}
                className={`h-11 rounded-xl border text-sm font-bold transition-colors ${colorClasses}`}
              >
                {type}
              </button>
            );
          })}
        </div>

        {isDateOverride && (
          <div>
            <label className="block text-xs font-bold text-text-secondary uppercase mb-2">
              Reason / Title
            </label>
            <input
              type="text"
              value={holidayReason}
              onChange={(e) => handleSetHolidayReason(e.target.value)}
              placeholder="e.g. Independence Day"
              className="w-full h-10 px-3 rounded-lg border border-border-default bg-white text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:border-primary"
            />
          </div>
        )}

        {/* Start / End Times */}
        {!isClosed && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-text-secondary uppercase mb-2">
                Start
              </label>
              <TimePickerInput
                value={startTime}
                onChange={handleSetStartTime}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-text-secondary uppercase mb-2">
                End
              </label>
              <TimePickerInput value={endTime} onChange={handleSetEndTime} />
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-left text-sm min-w-[500px] whitespace-nowrap">
          <thead className="bg-[#1F2241] text-white">
            <tr>
              <th className="px-4 py-3 font-medium text-xs">Date</th>
              <th className="px-4 py-3 font-medium text-xs">Day</th>
              <th className="px-4 py-3 font-medium text-xs">Type</th>
              <th className="px-4 py-3 font-medium text-xs">Time / Status</th>
              <th className="px-4 py-3 font-medium text-xs w-16 text-center">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {DAYS.map((d) => {
              const dKey = d.toLowerCase();
              const dConfig = weeklySchedule?.[dKey] || {};
              const isClosed = dConfig.type === 'closed';
              const isHalfDay = dConfig.type === 'half_day';
              const typeLabel = isClosed
                ? 'Closed'
                : isHalfDay
                  ? 'Half day'
                  : 'Full day';

              // Calculate upcoming date for this day of week
              const daysArr = [
                'Sunday',
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
              ];
              const today = new Date();
              const targetDay = daysArr.indexOf(d);
              const currentDay = today.getDay();
              let diff = targetDay - currentDay;
              if (diff < 0) diff += 7; // Show today if it matches
              const nextDate = new Date(today);
              nextDate.setDate(today.getDate() + diff);
              const dateString = nextDate.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              });

              return (
                <tr key={d} className="hover:bg-[#F9F9F9] transition-colors">
                  <td className="px-4 py-4 text-[#1F2241] font-medium">
                    {dateString}
                  </td>
                  <td className="px-4 py-4 text-[#6B7280]">{d}</td>
                  <td className="px-4 py-4">
                    <span
                      className={`px-2 py-1 rounded text-[11px] font-medium ${
                        isClosed
                          ? 'bg-[#FDECEC] text-[#F34343]'
                          : isHalfDay
                            ? 'bg-[#F7E6FE] text-[#B224EF]'
                            : 'bg-[#EBF4EA] text-[#50AB3D]'
                      }`}
                    >
                      {typeLabel}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-[#6B7280]">
                    {isClosed
                      ? '-'
                      : `${dConfig.working_hours_start || '09:00'} - ${dConfig.working_hours_end || '18:00'}`}
                  </td>
                  <td className="px-4 py-4 text-center">
                    <button
                      onClick={() => setActiveDay(d)}
                      className="text-[#F1A132] hover:opacity-80 transition-opacity"
                    >
                      <PencilIcon className="w-4 h-4 mx-auto" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
